import React from "react";
import CodeBlock from "@/components/CodeBlock";

const InfoDoc = () => {
  return (
    <section className="mb-20 text-slate-300">
      {/* TITLE */}
      <h2 className="text-3xl font-bold text-white mb-6">
        AxisNet CLI — Project Context (info)
      </h2>

      <p className="mb-6">
        The <code>info</code> command provides a detailed overview of your
        project's environment, directory structure, SDK configuration, and CLI
        session state. This command is essential when debugging, validating
        paths, or confirming correct initialization of the AxisNet project
        context.
      </p>

      {/* OUTPUT EXAMPLE */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mb-12">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-4">
          Sample Output (from <code>axisnet info</code>)
        </h3>

        <CodeBlock
          language="bash"
          code={`┏━AxisNet Project Context━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━┓
┃                                                                                                                         ┃
┃  System Summary                                                                                                         ┃
┃   CLI Context Initialized           : Yes                                                                               ┃
┃   .NET SDK Version                  : 8.0.404                                                                           ┃
┃   Target Framework                  : net8.0                                                                            ┃
┃   CLI Version                       : 1.0.0.0                                                                           ┃
┃   Operating System                  : Unix 14.5.0                                                                       ┃
┃                                                                                                                         ┃
┃  Project Summary                                                                                                        ┃
┃   Project Name                      : {{ProjectName}}                                                                   ┃
┃   Project Path                      : {ProjectURL}/AxisNetDocAPI/AxisNetDocAPI                                          ┃
┃   Test Layer Path                   : {ProjectURL}/AxisNetDocAPI/AxisNetDocAPI.Tests                                    ┃
┃   Domain Layer Path                 : {ProjectURL}/AxisNetDocAPI/AxisNetDocAPI.Domain                                   ┃
┃   Application Layer Path            : {ProjectURL}/AxisNetDocAPI/AxisNetDocAPI.Application                              ┃
┃   Infrastructure Layer Path         : {ProjectURL}/AxisNetDocAPI/AxisNetDocAPI.Infrastructure                           ┃
┃   Total Features                    : 3                                                                                 ┃
┃   Total Domain Entities             : 3                                                                                 ┃
┃                                                                                                                         ┃
┗━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━┛`}
        />
      </div>

      {/* BREAKDOWN */}
      <div className="space-y-12">
        {/* SYSTEM SUMMARY */}
        <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg">
          <h3 className="text-xl font-semibold text-emerald-400 mb-3">
            System Summary — Explained
          </h3>

          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>CLI Context Initialized</strong> — Indicates whether{" "}
              <code>axisnet init</code> has been run in this project.
            </li>
            <li>
              <strong>.NET SDK Version</strong> — Current SDK used by the CLI
              and project.
            </li>
            <li>
              <strong>Target Framework</strong> — Typically <code>net8.0</code>{" "}
              for AxisNet libraries.
            </li>
            <li>
              <strong>CLI Version</strong> — Current version of AxisNet CLI
              assembly.
            </li>
            <li>
              <strong>Operating System</strong> — Detected OS (Unix, Windows,
              MacOS).
            </li>
          </ul>
        </div>

        {/* PROJECT SUMMARY */}
        <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg">
          <h3 className="text-xl font-semibold text-emerald-400 mb-3">
            Project Summary — Explained
          </h3>

          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>Project Name</strong> — Extracted from{" "}
              <code>axisnet.json</code> or the first-time init configuration.
            </li>
            <li>
              <strong>Project Path</strong> — Root path of your API project
              (Presentation Layer).
            </li>
            <li>
              <strong>Test Layer Path</strong> — Where test projects are
              generated using <code>add test</code>.
            </li>
            <li>
              <strong>Domain Layer Path</strong> — Location of Domain entities.
            </li>
            <li>
              <strong>Application Layer Path</strong> — Home of Features,
              Services, Repositories.
            </li>
            <li>
              <strong>Infrastructure Layer Path</strong> — EF Core, Repos,
              Persistence, Email… all backend implementation code.
            </li>
            <li>
              <strong>Total Features</strong> — Number of feature folders under{" "}
              <code>/Application/Features/</code>.
            </li>
            <li>
              <strong>Total Domain Entities</strong> — Count of `.cs` files in
              the Domain layer.
            </li>
          </ul>
        </div>

        {/* HOW TO USE */}
        <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg">
          <h3 className="text-xl font-semibold text-emerald-400 mb-3">
            How to Use the <code>info</code> Command
          </h3>

          <p className="mb-4">
            You can run the command at any time to confirm CLI state or debug
            project path issues:
          </p>

          <CodeBlock language="bash" code={`axisnet info`} />

          <p className="mt-4">
            Recommended especially after changing folder structures, running
            <code>flags set validate-project-structure on</code>, or configuring
            new layers.
          </p>
        </div>

        {/* TROUBLESHOOTING */}
        <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg">
          <h3 className="text-xl font-semibold text-emerald-400 mb-3">
            Troubleshooting Tips
          </h3>

          <ul className="list-disc pl-6 space-y-2">
            <li>
              If <strong>CLI Context Initialized = No</strong>, re-run:
              <CodeBlock language="bash" code={`axisnet init`} />
            </li>
            <li>
              If Project Paths look wrong, check <code>axisnet.json</code> for
              incorrect <strong>Layer Paths</strong>.
            </li>
            <li>
              If feature or domain counts show <strong>0</strong>, ensure
              correct folder naming:
              <ul className="list-disc pl-6">
                <li>
                  <code>Features/{`{{DomainName}}`}s/</code>
                </li>
                <li>
                  <code>Domain/{`{{DomainName}}`}.cs</code>
                </li>
              </ul>
            </li>
          </ul>
        </div>
      </div>
    </section>
  );
};

export default InfoDoc;
